import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";

import { CommunicationType } from "@/enums/communicationLogs";
import { ShortcutAction } from "@/enums/keyboardShortcuts";
import {
  INBOX_SHORTCUT_NEXT_RECORD,
  INBOX_SHORTCUT_PREVIOUS_RECORD,
  INBOX_SHORTCUT_REPLY,
} from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useKeyboardShortcuts } from "@/hooks/keyboardShortcuts";
import { ROUTE_INBOX } from "@/router/routes";

import type { CommunicationLogBase } from "@/models/communicationLogs";
import type { Ref } from "vue";

interface UseInboxKeyboardShortcutsOptions {
  allDataPages: Ref<CommunicationLogBase[] | undefined>;
  isOpen: Ref<boolean>;
  focusedIndex: Ref<number>;
  toggleThread: (id: number, target?: "data") => void;
  closeThread: () => void;
  incrementThread: (step: number) => void;
  scrollTo: (index: number) => void;
}

export const useInboxKeyboardShortcuts = ({
  allDataPages,
  isOpen,
  focusedIndex,
  toggleThread,
  closeThread,
  incrementThread,
  scrollTo,
}: UseInboxKeyboardShortcutsOptions) => {
  const route = useRoute();
  const router = useRouter();
  const emitter = useEmitter();
  const { assignShortcutToAction } = useKeyboardShortcuts();

  const isInboxRoute = computed(() => route.name === ROUTE_INBOX);

  const isActiveElementEditable = (activeElement: Element | null) => {
    if (!activeElement) {
      return false;
    }
    const tagName = activeElement.tagName.toLowerCase();
    return (
      tagName === "input" ||
      tagName === "textarea" ||
      activeElement.getAttribute("contenteditable") === "true"
    );
  };

  const canPerformInboxAction = () => {
    if (!isInboxRoute.value) {
      return false;
    }

    const activeElement = document.activeElement;

    if (isActiveElementEditable(activeElement)) {
      return false;
    }

    const dialog = document.querySelector('[role="dialog"]');
    if (dialog) {
      return false;
    }

    if (activeElement?.closest("[data-keyboard-nav]")) {
      return false;
    }

    return true;
  };

  const canPerformEscapeAction = () => {
    if (!isInboxRoute.value) {
      return false;
    }

    const dialog = document.querySelector('[role="dialog"]');
    if (dialog) {
      return false;
    }

    return true;
  };

  const navigateToTab = (type?: CommunicationType) => {
    if (type === CommunicationType.note) {
      const query = { type };
      router.push({ name: ROUTE_INBOX, query });
      return;
    }
    const query = type ? { type, view: "inbox" } : { view: "inbox" };
    router.push({ name: ROUTE_INBOX, query });
  };

  const jumpToTop = () => {
    if (!allDataPages.value?.length) {
      return;
    }
    focusedIndex.value = 0;
    scrollTo(0);
  };

  const jumpToBottom = () => {
    if (!allDataPages.value?.length) {
      return;
    }
    const lastIndex = allDataPages.value.length - 1;
    focusedIndex.value = lastIndex;
    scrollTo(lastIndex);
  };

  // j/k: closes any open thread first, then moves focus in the list.
  // Arrow keys use handleNavigateUp/Down instead, which navigate
  // between threads when one is open (without closing it).
  const moveFocus = (step: number) => {
    if (!allDataPages.value?.length) {
      return;
    }

    if (isOpen.value) {
      closeThread();
    }

    if (focusedIndex.value === -1) {
      focusedIndex.value = 0;
    } else {
      const requestedStep = focusedIndex.value + step;
      const newIndex =
        step > 0
          ? Math.min(allDataPages.value.length - 1, requestedStep)
          : Math.max(0, requestedStep);
      focusedIndex.value = newIndex;
    }

    scrollTo(focusedIndex.value);
  };

  // Arrow keys: navigate between open threads, or move focus if no thread is open.
  // Unlike moveFocus (j/k), these do NOT close the current thread.
  const handleNavigateUp = () => {
    if (isOpen.value) {
      incrementThread(-1);
    } else if (allDataPages.value?.length) {
      const newIndex = Math.max(0, focusedIndex.value - 1);
      focusedIndex.value = newIndex;
      scrollTo(newIndex);
    }
  };

  const handleNavigateDown = () => {
    if (isOpen.value) {
      incrementThread(1);
    } else if (allDataPages.value?.length) {
      const newIndex = Math.min(
        allDataPages.value.length - 1,
        focusedIndex.value + 1
      );
      focusedIndex.value = newIndex;
      scrollTo(newIndex);
    }
  };

  const handleOpenThread = () => {
    if (!isOpen.value && allDataPages.value?.length) {
      const thread =
        focusedIndex.value >= 0
          ? allDataPages.value[focusedIndex.value]
          : allDataPages.value[0];
      if (thread) {
        toggleThread(thread.id);
      }
    }
  };

  const handleEscapeAction = () => {
    const activeElement = document.activeElement;
    if (isActiveElementEditable(activeElement)) {
      (activeElement as HTMLElement).blur();
      return;
    }

    if (isOpen.value) {
      closeThread();
    }
  };

  assignShortcutToAction(
    ShortcutAction.InboxNextThread,
    () => moveFocus(1),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxPreviousThread,
    () => moveFocus(-1),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxCloseThread,
    handleEscapeAction,
    canPerformEscapeAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxNavigateUp,
    handleNavigateUp,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxNavigateDown,
    handleNavigateDown,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxOpenThread,
    handleOpenThread,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxJumpToTop,
    jumpToTop,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxJumpToBottom,
    jumpToBottom,
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToAllActivities,
    () => navigateToTab(),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToEmails,
    () => navigateToTab(CommunicationType.email),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToSms,
    () => navigateToTab(CommunicationType.sms),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToCalls,
    () => navigateToTab(CommunicationType.call),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToTasks,
    () => navigateToTab(CommunicationType.task),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxGoToNotes,
    () => navigateToTab(CommunicationType.note),
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxReply,
    () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_REPLY);
      }
    },
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxNextRecord,
    () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_NEXT_RECORD);
      }
    },
    canPerformInboxAction
  );

  assignShortcutToAction(
    ShortcutAction.InboxPreviousRecord,
    () => {
      if (isOpen.value) {
        emitter.emit(INBOX_SHORTCUT_PREVIOUS_RECORD);
      }
    },
    canPerformInboxAction
  );
};
